import { WSAlert, WSElement, WSList } from "@wingspanhq/fe-component-library";
import { WSListProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSList/WSList.component";
import { IChannelNotificationResponse } from "@wingspanhq/notifications/dist/lib/interfaces";
import React, { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMemberProfile } from "../../../../query/users/queries";
import { AnimationGroup } from "../../../../shared/components/AnimationGroup";
import { useLocationStateAlert } from "../../../../shared/hooks/useLocationStateAlert";
import { selectorHasPayerContextUser } from "../../../../shared/selectors/selectorHasPayerContextUser";
import { selectorIsEnterpriseUser } from "../../../../shared/selectors/selectorIsEnterpriseUser";
import { AlertPayoutMethodMissingData } from "../../../Settings/components/AlertPayoutMethodMissingData";
import { BannerBankingVerification } from "../../../Wallet/components/BannerBankingVerification";
import { selectorAlerts } from "../../selectors/selectorAlerts";
import { BannerT4ATaxForm } from "../BannerT4ATaxForm";
import { NotificationAlert } from "./NotificationAlert";
import { useTaxVerificationVersion } from "../../../../shared/hooks/useTaxVerificationVersion";
import { ConfirmTaxInformationForLegacyUserAlert } from "../../../TaxDocuments/components/ConfirmTaxInformationForLegacyUserAlert";

type Props = {
  notifications: IChannelNotificationResponse[];
  showAlerts: boolean;
} & WSListProps;

export const AlertsList: React.FC<Props> = props => {
  const { notifications, showAlerts, ...listProps } = props;
  const userId = useUserId();

  const queryMember = useMemberProfile(userId);
  const queryInternalAccounts = useQueryInternalAccounts();
  const locationStateAlert = useLocationStateAlert();
  const { isLegacyOnlyTaxVerification } = useTaxVerificationVersion();

  const hasPendingAccount = useMemo(
    () =>
      (queryInternalAccounts.data || []).some(
        internalAccount =>
          (internalAccount.type === "Banking" ||
            internalAccount.type === "TaxWithholding") &&
          internalAccount.status === "Pending"
      ),
    [queryInternalAccounts.data]
  );

  const isPayerView =
    queryMember.data &&
    (selectorIsEnterpriseUser(queryMember.data.user) ||
      selectorHasPayerContextUser(queryMember.data.user));

  const alerts = useMemo(
    () => selectorAlerts(props.notifications),
    [props.notifications]
  );

  return (
    <AnimationGroup<WSListProps>
      component={WSList}
      gap="S"
      startDelay={500}
      type="slideDown"
      timeout={1000}
      ready={showAlerts}
      {...listProps}
    >
      {isLegacyOnlyTaxVerification && (
        <ConfirmTaxInformationForLegacyUserAlert />
      )}

      {locationStateAlert && <WSAlert {...locationStateAlert} />}

      {!isPayerView && hasPendingAccount && <BannerBankingVerification />}

      <AlertPayoutMethodMissingData />

      <BannerT4ATaxForm />

      {alerts.map(notification => (
        <NotificationAlert
          key={notification.channelMessageId}
          notification={notification}
        />
      ))}
      <WSElement key="spacer" mb="2XL" />
    </AnimationGroup>
  );
};
